import React, { useState, useEffect } from 'react';
import { userService, dashboardService } from '../services/api';
import { getErrorMessage } from '../utils/errorFormatter';

const refreshDashboard = () => {
  try {
    window.dispatchEvent(new Event('agroshop-dashboard-refresh'));
  } catch (error) {
    console.warn('Impossible de déclencher le rafraîchissement du dashboard.', error);
  }
};

export default function UserPanel({ vendeurs_recents = [], clients_recents = [], onChange = null }) {
  const [vendeurs, setVendeurs] = useState(vendeurs_recents);
  const [clients, setClients] = useState(clients_recents);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [nouveauNom, setNouveauNom] = useState('');
  const [nouveauTelephone, setNouveauTelephone] = useState('');
  const [nouveauEmail, setNouveauEmail] = useState('');
  const [nouveauMotDePasse, setNouveauMotDePasse] = useState('');

  const handleBlockUser = async (userId) => {
    setError('');
    setMessage('');
    if (!userId) return setError('Impossible: pas d\'identifiant utilisateur');
    try {
      await userService.block(userId);
      setVendeurs((prev) => prev.filter((u) => u.id !== userId));
      setClients((prev) => prev.filter((c) => c.user_id !== userId));
      setMessage('Utilisateur bloqué avec succès.');
      if (onChange) onChange(); else refreshDashboard();
    } catch (e) {
      setError(getErrorMessage(e));
    }
  };

  const handleDeleteUser = async (userId) => {
    setError('');
    setMessage('');
    if (!userId) return setError('Impossible: pas d\'identifiant utilisateur');
    try {
      await userService.delete(userId);
      setVendeurs((prev) => prev.filter((u) => u.id !== userId));
      setClients((prev) => prev.filter((c) => c.user_id !== userId));
      setMessage('Utilisateur supprimé.');
      if (onChange) onChange(); else refreshDashboard();
    } catch (e) {
      setError(getErrorMessage(e));
    }
  };

  const handleCreateVendeur = async (event) => {
    event.preventDefault();
    setError('');
    setMessage('');

    if (!nouveauNom || !nouveauTelephone || !nouveauMotDePasse) {
      return setError('Nom, téléphone et mot de passe sont requis.');
    }

    try {
      const vendeur = await userService.createVendor({
        nom_complet: nouveauNom,
        telephone: nouveauTelephone,
        mot_de_passe: nouveauMotDePasse,
        email: nouveauEmail,
      });
      setVendeurs((prev) => [vendeur, ...prev]);
      setMessage('Vendeur créé avec succès.');
      if (onChange) onChange(); else refreshDashboard();
      setNouveauNom('');
      setNouveauTelephone('');
      setNouveauEmail('');
      setNouveauMotDePasse('');
    } catch (e) {
      setError(getErrorMessage(e));
    }
  };

  useEffect(() => {
    setVendeurs(vendeurs_recents || []);
  }, [vendeurs_recents]);

  useEffect(() => {
    setClients(clients_recents || []);
  }, [clients_recents]);

  return (
    <section className="ag-dashboard-panel">
      <div className="ag-dashboard-panel-header">
        <div>
          <h3>Utilisateurs récents</h3>
          <p className="ag-muted">Vendeurs et clients séparés</p>
        </div>
      </div>

      <form className="ag-form" onSubmit={handleCreateVendeur} style={{ marginBottom: '18px' }}>
        <h4>Créer un vendeur</h4>
        <label>
          Nom complet
          <input
            type="text"
            value={nouveauNom}
            onChange={(e) => setNouveauNom(e.target.value)}
            placeholder="Nom et prénom"
            required
          />
        </label>
        <label>
          Téléphone
          <input
            type="text"
            value={nouveauTelephone}
            onChange={(e) => setNouveauTelephone(e.target.value)}
            placeholder="76000000"
            required
          />
        </label>
        <label>
          Adresse email (facultatif)
          <input
            type="email"
            value={nouveauEmail}
            onChange={(e) => setNouveauEmail(e.target.value)}
            placeholder="vendeur@example.com"
          />
        </label>
        <label>
          Mot de passe
          <input
            type="password"
            value={nouveauMotDePasse}
            onChange={(e) => setNouveauMotDePasse(e.target.value)}
            placeholder="Mot de passe"
            required
          />
        </label>
        <div className="ag-form-actions">
          <button className="ag-btn" type="submit">Créer vendeur</button>
        </div>
      </form>

      <div className="ag-user-groups">
        <div className="ag-user-group">
          <h4>Vendeurs récents</h4>
          <ul className="ag-user-list">
            {vendeurs.map((user) => (
              <li key={user.id} className="ag-user-item">
                <div>
                  <strong>{user.nom_complet}</strong>
                  <div className="ag-muted">{user.email || user.username}</div>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <span className={`ag-badge ag-badge--user ag-badge--${user.role}`}>{user.role}</span>
                  <button type="button" className="ag-btn-secondary" onClick={() => handleBlockUser(user.id)}>Bloquer</button>
                  <button type="button" className="ag-btn" onClick={() => handleDeleteUser(user.id)}>Supprimer</button>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="ag-user-group">
          <h4>Clients récents</h4>
          <ul className="ag-user-list">
            {clients.map((client) => (
              <li key={client.id} className="ag-user-item">
                <div>
                  <strong>{client.nom_complet}</strong>
                  <div className="ag-muted">{client.email || client.telephone || client.code_client}</div>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <span className="ag-badge ag-badge--client">client</span>
                  <button type="button" className="ag-btn-secondary" onClick={() => handleBlockUser(client.user_id)} disabled={!client.user_id}>Bloquer</button>
                  <button type="button" className="ag-btn" onClick={() => handleDeleteUser(client.user_id)} disabled={!client.user_id}>Supprimer</button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {message && <div className="ag-alert">{message}</div>}
      {error && <div className="ag-alert ag-alert-error">{error}</div>}
    </section>
  );
}

// Synchroniser l'état local si les props changent depuis le parent
// (par exemple après un rechargement du dashboard)
// On place l'effet après l'export pour conserver la signature d'export par défaut
